import {UI} from "../../../stem-core/ui/UIBase.js";
import {FileInput} from "../../../stem-core/ui/input/Input.jsx";
import {BlinkInputField} from "../Input.jsx";
import {BaseEnum, makeEnum} from "../../../stem-core/state/BaseEnum.js";
import {BaseInputElement} from "../../../stem-core/ui/input/BaseInputElement.js";
import {bytesToSize} from "../../../blinkpay/Utils.js";
import {SimpleTable} from "../../ui/SimpleTable.jsx";
import {CSVColumnMapper, CSVReader} from "../../../stem-core/base/CSV.js";
import {ArrayPaginator} from "../../../stem-core/state/pagination/EndpointPaginator";
import {StemDate} from "../../../stem-core/time/Date.js";

const MAX_CSV_FILE_SIZE = 30 << 20;

// Parse the CDS export's M/D/YY dates (e.g. "4/7/26") into a StemDate. Returns null if unparseable.
function parseCDSDate(value: string | undefined) {
    const parts = (value || "").split("/");
    if (parts.length !== 3) {
        return null;
    }
    const [month, day, year] = parts.map(part => parseInt(part, 10));
    if (!month || !day || Number.isNaN(year)) {
        return null;
    }
    const fullYear = year < 100 ? 2000 + year : year;
    return new StemDate(fullYear, month - 1, day);
}

@makeEnum
export class CSVFileFormat extends BaseEnum {
    static EMAIL_AUTODETECT; // Must have one of the following columns:
    static CDS_SUBSCRIBER; // Header row required; must have an "Email Address" column (the join key)
}

interface CSVFileInputOptions {
    fileFormat?: CSVFileFormat;
    maxFileSize?: number;
}

export class CSVFileInput extends BaseInputElement<any, CSVFileInputOptions> {
    declare csvColumnMapper?: CSVColumnMapper;
    declare paginator?: ArrayPaginator<any>;
    // The parsed data rows, minus the header when the format has one
    declare entries?: any[];
    declare fileInput: FileInput;

    error: string | null = null;

    setError(error: string) {
        this.error = error;
        this.redraw();
    }

    getDefaultOptions(options?: this["options"]) {
        return {
            ...super.getDefaultOptions(options),
            maxFileSize: MAX_CSV_FILE_SIZE,
            showPreviewTable: true,
            previewPageSize: 5,
            fileFormat: CSVFileFormat.EMAIL_AUTODETECT,
        }
    }

    loadCSVRows(csvRows: string[][]) {
        const {fileFormat} = this.options;

        // TODO move this code out when adding a new format
        if (fileFormat === CSVFileFormat.EMAIL_AUTODETECT) {
            const [headerRow, ...entries] = csvRows;
            this.entries = entries;
            this.csvColumnMapper = new CSVColumnMapper(headerRow, [
                [["email", "email_address", "email address", "email:"], {required: true}],
                ["name"],
            ]);

        } else if (fileFormat === CSVFileFormat.CDS_SUBSCRIBER) {
            // The CDS export is now keyed on email (no account number). Map by real header names so the format
            // is robust to column reordering; "Email Address" is required and errors out the upload if missing.
            const [headerRow, ...entries] = csvRows;
            this.entries = entries;
            this.csvColumnMapper = new CSVColumnMapper(headerRow, [
                [["email address", "email"], {key: "email", required: true}],
                ["first name", {key: "firstName"}],
                ["last name", {key: "lastName"}],
                ["start date", {key: "startDate", loader: parseCDSDate}],
                ["final expire date", {key: "endDate", loader: parseCDSDate}],
            ]);
        } else {
            throw "Unsupported file format";
        }

        if (this.entries.length === 0) {
            throw "No valid entries in file";
        }
    }

    async readFile() {
        const {maxFileSize} = this.options;

        this.error = null;
        this.paginator = null;
        this.csvColumnMapper = null;
        this.entries = null;

        const file = this.fileInput.getFile();
        if (!file) {
            this.setValue(null);
            return;
        }
        if (file.size > maxFileSize) {
            this.setError(`File size too large: ${bytesToSize(file.size)}. Max accepted is ${maxFileSize}.`);
            return;
        }

        try {
            const fileContent = await file.text();
            const csvReader = new CSVReader();
            const csvRows = csvReader.readInput(fileContent);
            this.loadCSVRows(csvRows);
        } catch (error) {
            this.setError(error);
            return;
        }

        this.paginator = new ArrayPaginator(this.entries, 5);
        this.setValue({entries: this.entries, columnMapper: this.csvColumnMapper});
    }

    getEntriesAsRows() {
        return this.getValue()?.entries || [];
    }

    getEntriesAsObjects(): Record<string, any>[] {
        const entries = this.getEntriesAsRows();
        const {columnMapper} = (this.getValue() || {});
        return entries.map((entry: string[]) => columnMapper.toObject(entry));
    }

    renderPreviewTable() {
        if (this.error || !this.csvColumnMapper) {
            return null;
        }

        const columns = this.csvColumnMapper.columns.map(col => [
            col.originalName, (entry: string[]) => entry[col.index], {style: {width: 200, minWidth: 200}}
        ]);

        const numSkippedColumns = this.csvColumnMapper.numOriginalColumns() - this.csvColumnMapper.numColumns();

        // TODO have a checkbox that can toggle to show all the columns
        return [
            <div style={{width: "100%"}}>
                {(numSkippedColumns > 0) && <div style={{paddingTop: 12}}>Only showing relevant columns,
                    ignored {numSkippedColumns} column(s).</div>}
                <SimpleTable columns={columns} paginator={this.paginator}/>
            </div>
        ];
    }

    render() {
        return [
            <div>
                <BlinkInputField label="Open a csv or tsv file:">
                    <FileInput
                        ref={"fileInput"}
                        multipleFiles={false}
                        fileTypes=".csv,.tsv"
                        onChange={() => this.readFile()}
                    />
                </BlinkInputField>
            </div>,
            this.error && <div style={{maxWidth: 375, paddingTop: 12, color: "red"}}>
                Error: {this.error}
            </div>,
            this.renderPreviewTable(),
        ]
    }
}